import React,{ useEffect, useReducer } from 'react'
import {getEvents,saveEvent,gapRanges,deleteEvent} from '../data-source/storedEvents'
import {getNewEvents,getWaypoints,eventsInRanges} from '../data-source/newEvents'
import {rangeFromYearAndMonth,rangeBeforeWithDurationInMonths} from '../timeConversion'
import moment from 'moment'

const EventsContext = React.createContext([])
export const EventsConsumer = EventsContext.Consumer

const reducer = (events,data) => {
    
    if( Array.isArray(data) ){
        return [...data];

    }else{
        const savedEvent = data
        let didUpdate = false;
        const updatedEvents = events.map( e => {
            if( e.date === savedEvent.date ){ 
                didUpdate = true
                return savedEvent;
            }
            return {...e}
        })

        return( didUpdate ? updatedEvents : [savedEvent, ...updatedEvents].sort( sortNewerFirst ) );
    }
}

const sortNewerFirst = (a,b) => {
    if( moment(a.date).isBefore(b.date) ) return(1);
    if( moment(a.date).isSame(b.date) ) return(0);
    return(-1); 
}

const speed = (waypoint) => {
    return !isNaN(parseFloat(waypoint['gprmc_gs'])) ? parseFloat(waypoint['gprmc_gs']) : 0;
}

const distanceFromWaypoints = (waypoints) => {
    const acc = waypoints.reduce(calculeteDistance,{distance: 0})
    return acc.distance.toFixed(2);
}

const calculeteDistance = (accumulator,waypoint) => {
    const next = {distance: 0, previousWp: waypoint};
        if(accumulator.previousWp){
            
            const avgSpeed = (speed(accumulator.previousWp) + speed(waypoint)) / 2;
            const elapsedTime = moment(waypoint.utc).diff(accumulator.previousWp.utc,'seconds')
            const distance = avgSpeed * elapsedTime / 3600 
            next.distance = accumulator.distance + distance;
        }
    return next;
}

export const EventsProvider = ({year,month,loadingStateHandler,children,errorHandler = () => {}}) => {
    
    const [events,dispatch] = useReducer( reducer, [])

    const saveHandler = (id,data) => {
        const promise = new Promise( (resolve,reject) => {
            errorHandler(false)
            saveEvent(id,data)
            .then( savedEvent => {
                dispatch(savedEvent)
                resolve()
            })
            .catch( e => {
                errorHandler(e);
                reject(e);
            })
        })
        return promise;
        
    }

    const deleteHandler = (id) => {
        errorHandler(false)
        deleteEvent(id)
        .then( response => {
            const updatedEvents = events.filter( e => e.id !== id )
            dispatch(updatedEvents)
        })
        .catch(errorHandler)
    }

    const loadEventsEffect = () => {
        errorHandler(false)
        dispatch([])
        loadingStateHandler(true)
        const range = rangeFromYearAndMonth(year,month)
        const initialRange = rangeBeforeWithDurationInMonths(2)
        const earlierRange = rangeBeforeWithDurationInMonths(12,2,range.start)
        const promiseLatestNewEvents = getNewEvents([initialRange])
        const promiseExistingEvents = getEvents(year,month,evs => {dispatch(evs)})

        const combineEvents = (existingEvents, newEvents, targetRange) => {
            const ranges = gapRanges(targetRange,existingEvents)
            const unclaimedNewEvents = eventsInRanges(newEvents,ranges)
            const combinedEvents = [...unclaimedNewEvents,...existingEvents]
            return combinedEvents.sort( sortNewerFirst );
        }

        Promise.all([promiseLatestNewEvents,promiseExistingEvents])
            .then( values => {
                const combinedEvents = combineEvents(values[1],values[0],range)
                dispatch(combinedEvents)
                loadingStateHandler(false)

                getNewEvents([earlierRange])
                    .then( earlierNewEvents => {
                        dispatch(combineEvents(combinedEvents,earlierNewEvents,range))
                    })
                    .catch( errorHandler )

            })
            .catch( errorHandler )
        
    }

    useEffect(loadEventsEffect ,[month,year]);

    const updateWaypointsForEvent = async (id) => {
        const event = events.find( e => e.id === id )
      
        if(event && event.waypoints.length < 3 && event.waypoints.length > 1){
            const start = event.waypoints[0].date
            const end = event.waypoints[1].date
            const waypoints = await getWaypoints(start,end)
            
            if(Array.isArray(waypoints) && waypoints.length > 1){
                const sortedWaypoints = waypoints.sort( (a,b) => {
                    if( moment(a.utc).isBefore(b.utc) ) return(-1);
                    if( moment(a.utc).isSame(b.utc) ) return(0);
                    return(0); 
                })

                const updatedEvent = {...event,waypoints: sortedWaypoints.map( wp => {return({...wp,date:wp.utc})} ),distance: event.distance || distanceFromWaypoints(waypoints)}
                
                dispatch(updatedEvent)
            }
            
        }
    }

    return <EventsContext.Provider value={{events: events, updateWaypointsForEvent: updateWaypointsForEvent, saveEventHandler: saveHandler, deleteEventHandler: deleteHandler}}>{children}</EventsContext.Provider>
}

export default EventsContext